// ============================================================================
// Códigos de error de la API (`meta.code`) — contrato estable con el backend.
// ============================================================================
// Todo error de la API viaja en el mismo sobre
// (`torre-de-control-api/functions/src/middlewares/errorHandler.js`):
//
//   { meta: { error: true, status, url, message, code? } }
//
// `meta.message` NO sirve para ramificar: el errorHandler lo pasa por
// `req.t(err.message)`, así que lo que llega es prosa TRADUCIDA y cambia con
// el `?lang=` de la petición (p. ej. la clave `companySuspended` llega como
// "Company suspended successfully" en inglés y "Compañía suspendida
// correctamente" en español). `meta.code` es el identificador estable: lo pone
// el backend explícitamente y no pasa por i18n.
//
// El estado HTTP tampoco basta: un 403 puede ser compañía suspendida, falta de
// permisos (mesa de servicio, chat) o sesión inválida, y cada uno se maneja
// distinto. Siempre se comprueba estado + código.
//
// Módulo SIN dependencias a propósito: lo importan tanto `utils/api.ts` (con
// axios, firebase y sweetalert detrás) como las pantallas de login/registro en
// sus pruebas, que no deben arrastrar ese árbol.
// ============================================================================

export const API_ERROR_CODES = {
  /** 403 de login/registro: la compañía del usuario no está activa. */
  COMPANY_SUSPENDED: "COMPANY_SUSPENDED",
  /** 403 de autorización: la sesión es válida pero falta permiso. */
  PERMISSION_DENIED: "PERMISSION_DENIED",
  /** 403: la acción es exclusiva de un administrador de plataforma (#642). */
  PLATFORM_ADMIN_REQUIRED: "PLATFORM_ADMIN_REQUIRED",
  /** 403: la sesión no tiene el derecho que exige la ruta (#658). */
  ENTITLEMENT_REQUIRED: "ENTITLEMENT_REQUIRED",
  /** 403: pidió datos de otra compañía sin ser administrador de plataforma (#1579). */
  COMPANY_SCOPE_FORBIDDEN: "COMPANY_SCOPE_FORBIDDEN",
  SANDBOX_READ_ONLY: "SANDBOX_READ_ONLY",
  /** 403: el correo no es de un dominio permitido para este tenant (#636). */
  EMAIL_DOMAIN_NOT_ALLOWED: "EMAIL_DOMAIN_NOT_ALLOWED",
  /** 403: hay que verificar el correo antes de crear la cuenta (#636). */
  EMAIL_NOT_VERIFIED: "EMAIL_NOT_VERIFIED",
  /**
   * 409 al encender el bot de un grupo de WhatsApp (#556): el aviso de
   * privacidad anterior PUDO llegar. Hay que decir si llegó
   * (`noticeConfirmed`) o si se publica otra vez (`republishNotice`).
   */
  WA_BOT_NOTICE_IN_DOUBT: "WA_BOT_NOTICE_IN_DOUBT",
  /**
   * 402 al crear un usuario, vehículo o dispositivo (CC-CONSOLE #1613): el
   * plan de la compañía ya no da para más. `meta.details` trae
   * `{ upgrade: true, entity, limit, used, licenseId, licenseName }`; la consola
   * lo muestra con «Mejorar plan» (utils/planLimitNotice).
   */
  PLAN_LIMIT_REACHED: "PLAN_LIMIT_REACHED",
  /**
   * 402 al ACEPTAR una invitación cuando el plan de la compañía que invitó ya
   * está lleno. Quien lo ve es el invitado, que no administra esa compañía:
   * NO lleva «Mejorar plan» (el interceptor no lo trata como PLAN_LIMIT_REACHED)
   * y el registro muestra el mensaje del API.
   */
  PLAN_LIMIT_INVITEE: "PLAN_LIMIT_INVITEE",
} as const;

export type ApiErrorCode =
  (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

type ApiErrorShape = {
  response?: {
    status?: number;
    data?: { meta?: { code?: string } } | unknown;
  };
};

/** `meta.code` del sobre de error, o `undefined` si no viene. */
export const apiErrorCode = (error: unknown): string | undefined => {
  const data = (error as ApiErrorShape)?.response?.data as
    | { meta?: { code?: string } }
    | undefined;
  const code = data?.meta?.code;
  return typeof code === "string" ? code : undefined;
};

/** ¿El error es el 403 de compañía suspendida (login y registro)? */
export const isCompanySuspendedError = (error: unknown): boolean =>
  (error as ApiErrorShape)?.response?.status === 403 &&
  apiErrorCode(error) === API_ERROR_CODES.COMPANY_SUSPENDED;

/** ¿El 409 del bot de WhatsApp pide decidir sobre un aviso que pudo llegar? */
export const isWaBotNoticeInDoubtError = (error: unknown): boolean =>
  (error as ApiErrorShape)?.response?.status === 409 &&
  apiErrorCode(error) === API_ERROR_CODES.WA_BOT_NOTICE_IN_DOUBT;

/** ¿El error es el 403 de dominio de correo no permitido (#636)? */
export const isEmailDomainNotAllowedError = (error: unknown): boolean =>
  (error as ApiErrorShape)?.response?.status === 403 &&
  apiErrorCode(error) === API_ERROR_CODES.EMAIL_DOMAIN_NOT_ALLOWED;

/** ¿El error es el 403 de acción exclusiva de Sistemas Internos (#642)? */
export const isPlatformAdminRequiredError = (error: unknown): boolean =>
  (error as ApiErrorShape)?.response?.status === 403 &&
  apiErrorCode(error) === API_ERROR_CODES.PLATFORM_ADMIN_REQUIRED;

/** ¿El error es el 403 de correo sin verificar (#636)? */
export const isEmailNotVerifiedError = (error: unknown): boolean =>
  (error as ApiErrorShape)?.response?.status === 403 &&
  apiErrorCode(error) === API_ERROR_CODES.EMAIL_NOT_VERIFIED;
